import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { concatMap, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  NoticeMediaUploadError,
  NoticePayload,
  NoticePromptView,
  NoticeTable,
  NoticeView
} from '../dashboard/notice-board/notice.model';
import { ProcessedFile } from '../helpers/file.helper';
import { CurrentUserService } from './currentUser.service';

@Injectable({
  providedIn: 'root'
})
export class NoticeService {
  #http = inject(HttpClient);
  #currentUserService = inject(CurrentUserService);
  unseenNotices: WritableSignal<NoticePromptView[]> = signal([]);

  #handleError(error: HttpErrorResponse): Observable<never> {
    console.error(error.message);
    return throwError(() => error);
  }

  #getNoticeFormData({ notice, medias }: NoticePayload): [FormData, Observable<string>] {
    const noticeBlob = new Blob([JSON.stringify(notice)], { type: 'application/json' });
    const mediasSize = medias.length ? medias.map(({ blob }) => blob.size).reduce((acc, current) => acc + current) : 0;
    const nginxRequestLimit = 10 * 1024 * 1024;

    if (noticeBlob.size + mediasSize > nginxRequestLimit) {
      const errorMessage =
        'Failed to add notice - attachments cannot exceed 10Mb in total. Please try again with less attachments. ' +
        'Attachments can continue to be added to the notice after it has been added to the board.';

      return [null, of(errorMessage)];
    }

    const formData = new FormData();
    formData.append('notice', noticeBlob);
    medias.forEach(({ blob, name }: ProcessedFile) => formData.append('media', blob, name));

    return [formData, null];
  }

  fetchUnseenNoticesIfPermissible(): void {
    if (
      this.#currentUserService?.currentUserValue?.privileges?.length === 0 ||
      this.#currentUserService?.currentUserValue?.privileges?.every((privilege) =>
        ['REPADMIN', 'MLADMIN'].includes(privilege)
      )
    ) {
      this.getUnseenNotices().subscribe();
    }
  }

  // Rests for Admins
  getNoticeTable(pageNumber = 0): Observable<NoticeTable> {
    return this.#http.get<NoticeTable>(`ui/admin/noticeBoard?page=${pageNumber}`).pipe(catchError(this.#handleError));
  }

  getNotice(id: number): Observable<NoticeView> {
    return this.#http.get<NoticeView>(`ui/admin/notice/${id}`).pipe(catchError(this.#handleError));
  }

  getNoticeMediaUrl(mediaId: number): Observable<string> {
    return this.#http.post<string>(`ui/mainService/notice/media/${mediaId}`, null).pipe(catchError(this.#handleError));
  }

  addNotice(noticePayload: NoticePayload): Observable<NoticeMediaUploadError | string> {
    const [formData, error] = this.#getNoticeFormData(noticePayload);

    return formData
      ? this.#http.post<NoticeMediaUploadError>(`ui/admin/notice`, formData).pipe(catchError(this.#handleError))
      : error.pipe(concatMap(throwError));
  }

  updateNotice(noticePayload: NoticePayload): Observable<NoticeMediaUploadError | string> {
    const [formData, error] = this.#getNoticeFormData(noticePayload);

    return formData
      ? this.#http
          .post<NoticeMediaUploadError>(`ui/admin/notice/${noticePayload.notice.id}`, formData)
          .pipe(catchError(this.#handleError))
      : error.pipe(concatMap(throwError));
  }

  archiveNotice(noticeId: number): Observable<void> {
    return this.#http.delete<void>(`ui/admin/notice/${noticeId}`).pipe(catchError(this.#handleError));
  }

  // Rests for Repairers
  getUnseenNotices(): Observable<NoticePromptView[]> {
    return this.#http.get<NoticePromptView[]>('ui/mainService/unseenNotices').pipe(
      tap((unseenNotices: NoticePromptView[]) => this.unseenNotices.set(unseenNotices)),
      catchError(this.#handleError)
    );
  }

  acknowledgeNotice(noticeId: number): Observable<void> {
    return this.#http.post<void>(`ui/mainService/ackNotice/${noticeId}`, null).pipe(catchError(this.#handleError));
  }

  getNoticeBoard(pageNumber = 0): Observable<NoticeTable> {
    return this.#http
      .get<NoticeTable>(`ui/mainService/noticeBoard?page=${pageNumber}`)
      .pipe(catchError(this.#handleError));
  }
}
